import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { fetchAll } from './fetchall'
import type { Agent, AgentTag } from './types'
import type { View } from './views'
import {
  EMPTY as EMPTY_SELECTION,
  addAll,
  pruneToRoster,
  removeAll,
  toggle as toggleOne,
  type Selection,
  type SelectionUndo,
} from './selection'
import {
  ANY,
  DEFAULT_SORT,
  FILTER_BY_KEY,
  SORT_BY_KEY,
  activeFilterKeys,
  optionsFor as optionsForAgents,
  selectAgents,
  type FilterContext,
  type FilterState,
  type TabKey,
} from './filters'

export { ANY } from './filters'
export type { TabKey } from './filters'

export type Option = { value: string; label: string }

/**
 * How long a tab may sit on a background fetch before coming back into view
 * asks for a fresh one. See the `visibilitychange` effect in
 * `RosterProvider` for why this exists — three minutes catches "left open
 * overnight" and "left open across the morning sync" without turning a
 * quick alt-tab into a re-fetch.
 */
const REVALIDATE_AFTER_MS = 3 * 60_000

type RosterValue = {
  agents: Agent[] | null
  error: string | null
  tagsByAgent: Map<string, string[]>
  /**
   * Set when the tags failed to load — which means every "do not email this
   * person" tag is missing and nothing on screen would show it. Anything that
   * can send has to check this and stop.
   */
  tagsError: string | null
  /** The tag fetch has come back, one way or the other. */
  tagsLoaded: boolean
  /** A tag fetch is in flight — so "Try again" can say it is trying. */
  tagsLoading: boolean
  /** Same, for the roster. Both retry buttons need it or one of them reads dead. */
  agentsLoading: boolean
  notesByAgent: Map<string, string>
  search: string
  setSearch: (v: string) => void
  filters: FilterState
  setFilter: (key: string, value: string) => void
  clearFilters: () => void
  activeFilters: number
  optionsFor: (key: string) => Option[]
  sortKey: string
  setSortKey: (v: string) => void
  includeDeparted: boolean
  setIncludeDeparted: (v: boolean) => void
  visible: (tab: TabKey) => Agent[]
  counts: Record<TabKey, number>
  countFor: (view: View) => number
  applyView: (view: View) => void
  currentView: (label: string, tab?: TabKey) => View
  reloadTags: () => void
  reloadNotes: () => void
  /**
   * Try the roster again. Without this, a failed agent fetch leaves every
   * screen that needs the roster stuck on "Loading…" with no way out but a
   * page reload she has no reason to believe would help.
   */
  reloadAgents: () => void

  /**
   * The mailing list. Who Renee has ticked, kept in memory only — it is a list
   * for one job, not a record. See `selection.ts` for why it is a Set and why
   * it deliberately survives a filter change.
   */
  selection: Selection
  selectedCount: number
  isSelected: (id: string) => boolean
  toggleSelected: (id: string) => void
  /** Add agents, returning how many were genuinely new. */
  addToSelection: (ids: readonly string[]) => number
  /** Remove agents, returning how many were actually in the list. */
  removeFromSelection: (ids: readonly string[]) => number
  /**
   * Empty the list. `cause` is only what the undo will *say* — 'sent' when the
   * composer spent the list on a send that went through, 'cleared' for every
   * deliberate emptying. It changes no behaviour.
   */
  clearSelection: (cause?: 'cleared' | 'sent') => void
  /**
   * Put back what the last shrink removed — Clear all, "Take off N" (which on
   * an unfiltered list removes everybody just as thoroughly), a cancelled
   * composer, or a send. Null when there is nothing to put back. `count` is on
   * it so the button can say the number, and `what` so it can say why.
   */
  undoSelection: null | { count: number; what: 'cleared' | 'removed' | 'sent'; run: () => void }
  /** Show only the ticked agents. A scope, so the tab, filters and search still apply. */
  selectedOnly: boolean
  setSelectedOnly: (v: boolean) => void
}

const RosterContext = createContext<RosterValue | null>(null)

/**
 * Turn a database error into something Renee can act on.
 *
 * "permission denied for table agents" is what an expired session looks like
 * from here: the token dies, PostgREST falls back to the `anon` role, and anon
 * has deliberately been granted nothing. It reads like a catastrophic
 * permissions failure and means "sign in again".
 */
function readable(message: string): string {
  const m = message.toLowerCase()
  if (m.includes('permission denied') || m.includes('jwt') || m.includes('not authorized')) {
    return 'Your sign-in expired. Refresh the page and sign in again — nothing is wrong with your data.'
  }
  return message
}

export function RosterProvider({ children }: { children: ReactNode }) {
  const [agents, setAgents] = useState<Agent[] | null>(null)
  const [tags, setTags] = useState<AgentTag[]>([])
  const [error, setError] = useState<string | null>(null)
  const [tagsError, setTagsError] = useState<string | null>(null)
  /**
   * Whether the tags have been *answered for* — not whether there are any.
   *
   * The composer freezes its list of people the moment it opens, and a tag
   * filter answered against an empty map matches nobody. Without this, opening
   * the composer in the gap between the roster arriving and the tags arriving
   * froze it at zero agents, permanently, with no explanation. Two independent
   * fetches; nothing orders them.
   */
  const [tagsLoaded, setTagsLoaded] = useState(false)
  const [tagsLoading, setTagsLoading] = useState(true)
  /** In flight, so "Try again" can say it is trying rather than repainting. */
  const [agentsLoading, setAgentsLoading] = useState(true)
  /**
   * When the roster and tags were last actually fetched — read by the
   * revalidate-on-focus effect below, not rendered anywhere. `useRef`, not
   * state: updating it must never itself trigger a re-render or re-run the
   * fetch effects that set it.
   */
  const agentsFetchedAt = useRef(0)
  const tagsFetchedAt = useRef(0)

  const [search, setSearch] = useState('')
  const [filters, setFilters] = useState<FilterState>({})
  const [sortKey, setSortKey] = useState<string>(DEFAULT_SORT)
  const [includeDeparted, setIncludeDeparted] = useState(false)
  const [tagNonce, setTagNonce] = useState(0)
  const [agentNonce, setAgentNonce] = useState(0)
  const [notes, setNotes] = useState<{ agent_id: string; body: string }[]>([])
  const [noteNonce, setNoteNonce] = useState(0)
  const [selection, setSelection] = useState<Selection>(EMPTY_SELECTION)
  const [selectedOnly, setSelectedOnlyRaw] = useState(false)
  const [undone, setUndone] = useState<SelectionUndo | null>(null)

  /**
   * The date, watched.
   *
   * "Licence expiring within 30 days" is answered against today, and Renee
   * leaves this open for days. Without this the tab counts — computed once per
   * memo — would still be yesterday's while the list, recomputed on the next
   * render for any reason, quietly gained a row. A tab saying 41 above a list
   * of 42 is the kind of thing that makes someone stop trusting the screen.
   */
  const [today, setToday] = useState(() => new Date().toDateString())
  useEffect(() => {
    const t = window.setInterval(() => {
      const now = new Date().toDateString()
      setToday((prev) => (prev === now ? prev : now))
    }, 60_000)
    return () => window.clearInterval(t)
  }, [])

  /**
   * The roster, in pages.
   *
   * 785 today, and PostgREST silently cuts a plain select at 1000. Nobody would
   * notice the day it crossed — the list would simply be missing people, with
   * no error and no gap in the alphabet worth remarking on. See `fetchall.ts`.
   */
  useEffect(() => {
    let cancelled = false
    // `rezen_id` is the primary key and breaks every tie. Ordering by
    // `last_name` alone is not a total order — there are plenty of Smiths —
    // and offset paging over a partial order drops and duplicates rows.
    setAgentsLoading(true)
    void fetchAll<Agent>('agents', '*', ['last_name', 'rezen_id']).then(
      ({ rows, error, truncated }) => {
        if (cancelled) return
        setAgentsLoading(false)
        agentsFetchedAt.current = Date.now()
        if (error) setError(readable(error))
        else {
          setAgents(rows)
          if (truncated) {
            setError('Only part of the roster could be read — some agents are missing. Tell Chris.')
          }
        }
      },
    )
    return () => {
      cancelled = true
    }
  }, [agentNonce])

  /**
   * Tags, and the error if they do not come.
   *
   * This used to throw the error away and set an empty list. That is the most
   * dangerous kind of failure this app can have: `no-outreach` and
   * `no-bulk-email` are tags, so a failed load does not *look* like anything —
   * the roster is fine, every filter works, the tag column is just empty — and
   * every suppression silently stops applying. The first symptom would be an
   * agent who asked not to be emailed being emailed.
   *
   * So it is kept, and the composer refuses to queue anything while it is set.
   *
   * It is also read in pages. A tag table over 1000 rows comes back cut short
   * with **no error at all**, so the check below would pass while some agent's
   * `no-bulk-email` had quietly ceased to exist. An error you can catch is the
   * lesser of the two problems here.
   */
  useEffect(() => {
    let cancelled = false
    setTagsLoading(true)
    // `(agent_id, tag)` is the primary key — a total order, which offset
    // paging requires and which this table has no natural one of.
    void fetchAll<AgentTag>('agent_tags', '*', ['agent_id', 'tag']).then(
      ({ rows, error, truncated }) => {
        if (cancelled) return
        setTagsLoading(false)
        setTagsLoaded(true)
        tagsFetchedAt.current = Date.now()
        if (error) {
          console.error('agent tags failed to load', error)
          setTagsError(readable(error))
        } else if (truncated) {
          setTagsError('Only part of the tag list could be read, so some may be missing.')
        } else {
          setTagsError(null)
        }
        setTags(rows)
      },
    )
    return () => {
      cancelled = true
    }
  }, [tagNonce])

  /**
   * Re-fetch the roster and tags when a long-idle tab comes back into view.
   *
   * Added 2026-09-11. Both fetches above run exactly once, on mount, and
   * nothing before this refreshed them again — so a tab left open across the
   * daily sync (the early-morning run that creates a genuinely new agent's
   * `agents` row) keeps serving that stale, agent-shaped-hole snapshot
   * indefinitely. `recheckAtHandoff` then reads it correctly and reports
   * wrongly: "This agent is no longer in the roster" for someone who, in
   * the database sitting one request away, has existed for hours. Bonita
   * Vinson's welcome card did exactly this on 2026-09-11 — Chris confirmed
   * she was already in Metabase — the same shape as Joy Lowe's case two
   * days earlier (`claude/joy-lowe-new-joiner-self-heal-2026-09-09.md`),
   * which "fixed itself" only because checking it the next day happened to
   * mean a fresh page load. This is the fix that makes that not depend on
   * anyone happening to reload: `visibilitychange` fires when a background
   * tab is switched back to, and a stale-for-a-while fetch is refreshed
   * right then, before the next click can act on it.
   *
   * Throttled to once every `REVALIDATE_AFTER_MS`, not on every tab switch —
   * alt-tabbing back and forth all day must not turn into a roster refetch
   * on each glance. `agentsFetchedAt/tagsFetchedAt` are stamped by the fetch
   * effects above (on success AND failure — a failing endpoint should not be
   * hammered every time the tab regains focus either) and read here, never
   * written here, so this effect only ever decides whether to ask, never
   * how.
   */
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return
      const now = Date.now()
      if (now - agentsFetchedAt.current > REVALIDATE_AFTER_MS) {
        setError(null)
        setAgentsLoading(true)
        setAgentNonce((n) => n + 1)
      }
      if (now - tagsFetchedAt.current > REVALIDATE_AFTER_MS) {
        setTagsError(null)
        setTagsLoading(true)
        setTagNonce((n) => n + 1)
      }
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [])

  /**
   * Every note, once, so the search box can reach them.
   *
   * One row per agent and only the body — 785 short strings. The detail panel
   * still loads its own copy when an agent is opened, because that one has to
   * be current enough to edit; this one only has to be current enough to find.
   */
  useEffect(() => {
    let cancelled = false
    void fetchAll<{ agent_id: string; body: string }>('agent_notes', 'agent_id,body', ['agent_id']).then(
      ({ rows, error }) => {
        if (cancelled) return
        // Logged rather than shown: notes failing to load makes searches quietly
        // narrower, and silence about it is how that goes unnoticed for weeks.
        // Nothing sends on the strength of a note, so this one does not block.
        if (error) console.error('notes for search failed', error)
        setNotes(rows)
      },
    )
    return () => {
      cancelled = true
    }
  }, [noteNonce])

  /**
   * Anyone no longer in the roster leaves the selection.
   *
   * Without this, an id removed at cutover would sit in the count forever —
   * "49 selected" above a list that can only ever show 48, and no way to find
   * the missing one. `pruneToRoster` returns the same Set when nothing changed,
   * so this does not loop.
   */
  useEffect(() => {
    if (!agents) return
    setSelection((prev) => pruneToRoster(prev, agents))
  }, [agents])

  /**
   * "Show only these" turns itself off when the list empties.
   *
   * Otherwise clearing the selection while it is on leaves "No agents match"
   * on screen with nothing saying why, and the way out — the toggle — is the
   * one thing she has no reason to look at.
   */
  useEffect(() => {
    if (selectedOnly && selection.size === 0) setSelectedOnlyRaw(false)
  }, [selectedOnly, selection])

  /**
   * The heavy derivations, kept off the ticking path.
   *
   * `counts` runs `selectAgents` four times over 786 agents and `visible` adds
   * a sort on top. `selection` is a dependency of the value below, so without
   * this every single tick — 273 of them while building a list by hand — would
   * redo all of it. Nothing here reads the selection unless `selectedOnly` is
   * on, so while it is off the tick cannot change the answer and must not
   * invalidate the memo.
   */
  const selectionForScope = selectedOnly ? selection : null

  const notesByAgent = useMemo(() => {
    const m = new Map<string, string>()
    for (const n of notes) if (n.body?.trim()) m.set(n.agent_id, n.body)
    return m
  }, [notes])

  const tagsByAgent = useMemo(() => {
    const m = new Map<string, string[]>()
    for (const t of tags) {
      const list = m.get(t.agent_id) ?? []
      list.push(t.tag)
      m.set(t.agent_id, list)
    }
    return m
  }, [tags])

  /**
   * Everything expensive, memoised without the raw selection in its deps.
   *
   * `counts` runs `selectAgents` four times across 786 agents and `visible`
   * sorts on top of that; `filters.ts` already measured 785 rows of per-row
   * formatting at ~130ms. Building a mailing list by hand is hundreds of ticks,
   * and while "Show only these" is off a tick cannot change any of these
   * answers — so it must not invalidate them. `selectionForScope` is null
   * whenever the scope is off, which is what keeps this stable.
   */
  const derived = useMemo(() => {
    const all = agents ?? []
    const ctx: FilterContext = { tagsByAgent, notesByAgent, selection: selectionForScope ?? undefined }

    // Departed agents are excluded from the dropdowns unless they are being
    // shown, or the lists offer teams and dates nobody visible has.
    const optionPool = includeDeparted ? all : all.filter((a) => a.is_active)

    const optionsFor = (key: string): Option[] => {
      const opts = optionsForAgents(key, optionPool, ctx)
      const chosen = filters[key]
      /**
       * A value can leave the pool while it is still applied — tick "Include
       * departed", pick a team only departed agents are on, untick it again.
       * Or filter by a tag, then delete the last one.
       *
       * A `<select>` whose value matches no option deselects everything and
       * paints blank, so the screen would be an empty dropdown above an empty
       * list with nothing saying why. Keep the value, and say what it means.
       */
      if (chosen && !opts.some((o) => o.value === chosen)) {
        const def = FILTER_BY_KEY.get(key)
        opts.push({
          value: chosen,
          label: `${def?.display ? def.display(chosen) : chosen} — no matches`,
        })
      }
      return opts
    }

    /**
     * Snap a view's values onto the ones the dropdowns actually offer.
     *
     * Filter values are matched literally, and Pearl asked for
     * `license_window: "already expired"` — lower case. Nobody has that, so the
     * offer looked right, the button worked, and the list came back empty. The
     * dropdown said "already expired — no matches", which is honest and no help.
     *
     * Telling her in the prompt to use exact values did not work and was never
     * going to: **normalise what a model gives you rather than relying on it to
     * reproduce a string exactly.** Only case is forgiven here — a value that
     * genuinely matches nobody still comes back empty and still says so.
     */
    const resolve = (view: View): FilterState => {
      const next: FilterState = {}
      for (const f of view.filters) {
        if (!f.value || f.value === ANY) continue
        const options = optionsForAgents(f.key, optionPool, ctx)
        const exact = options.find((o) => o.value === f.value)
        const insensitive = options.find((o) => o.value.toLowerCase() === f.value.toLowerCase())
        next[f.key] = exact?.value ?? insensitive?.value ?? f.value
      }
      return next
    }

    const select = (tab: TabKey) =>
      selectAgents(all, { tab, filters, search, includeDeparted, selectedOnly }, ctx)

    const sort = SORT_BY_KEY.get(sortKey) ?? SORT_BY_KEY.get(DEFAULT_SORT)!
    const visible = (tab: TabKey) => select(tab).sort(sort.compare)

    const counts = {
      all: select('all').length,
      capped: select('capped').length,
      elite: select('elite').length,
      leaders: select('leaders').length,
    } as Record<TabKey, number>

    return { optionsFor, resolve, visible, counts, all, ctx }
    // `today` is in here because the licence-status filter is answered against
    // it. Nothing reads the variable directly.
  }, [
    agents,
    tagsByAgent,
    notesByAgent,
    search,
    filters,
    sortKey,
    includeDeparted,
    today,
    selectionForScope,
  ])

  const value = useMemo<RosterValue>(() => {
    const { optionsFor, resolve, visible, counts, all, ctx } = derived
    return {
      agents,
      error,
      tagsByAgent,
      tagsError,
      tagsLoaded,
      tagsLoading,
      agentsLoading,
      notesByAgent,
      search,
      setSearch,
      filters,
      setFilter: (key, v) =>
        setFilters((prev) => {
          const next = { ...prev }
          if (v === ANY) delete next[key]
          else next[key] = v
          return next
        }),
      clearFilters: () => {
        setFilters({})
        setSearch('')
      },
      activeFilters: activeFilterKeys(filters).length,
      optionsFor,
      sortKey,
      setSortKey,
      includeDeparted,
      setIncludeDeparted,
      visible,
      counts,
      /**
       * How many agents a view would show, without going anywhere. The gaps
       * screen needs the number before Renee decides whether to look, and
       * Pearl's offer shows it before Renee decides whether to press.
       */
      countFor: (view: View) =>
        selectAgents(
          all,
          {
            tab: view.tab,
            filters: resolve(view),
            search: view.search,
            includeDeparted: view.include_departed,
          },
          ctx,
        ).length,
      /**
       * Put a view on screen. One implementation, used by Pearl's offer, the
       * gaps screen and a saved view — three routes to the same place, so they
       * cannot drift into meaning different things.
       */
      applyView: (view: View) => {
        /**
         * Applying a view leaves the mailing-list scope.
         *
         * `countFor` deliberately answers without it, so What's Missing can say
         * "236 with no mailing address". Landing on a screen still scoped to the
         * list would show 4, and the number she pressed would be a number the
         * destination cannot produce.
         */
        setSelectedOnlyRaw(false)
        setFilters(resolve(view))
        setSearch(view.search)
        setIncludeDeparted(view.include_departed)
        setSortKey(view.sort ?? DEFAULT_SORT)
      },
      /**
       * What is on screen now, ready to be saved under a name. The tab has to
       * be passed in — the roster does not know which one is showing, and
       * saving "the team leaders" as "everyone" loses the whole point of it.
       */
      currentView: (label: string, tab: TabKey = 'all') => ({
        label,
        filters: Object.entries(filters).map(([key, value]) => ({ key, value })),
        tab,
        search,
        include_departed: includeDeparted,
        sort: sortKey,
      }),
      /**
       * Clears the error as well as re-fetching.
       *
       * Otherwise pressing "Try again" and failing again repaints a
       * byte-identical banner, and there is no way to tell whether the button
       * did anything at all. `tagsLoading` is what makes the difference visible
       * while it is in flight.
       */
      reloadTags: () => {
        setTagsError(null)
        setTagsLoading(true)
        setTagNonce((n) => n + 1)
      },
      reloadNotes: () => setNoteNonce((n) => n + 1),
      reloadAgents: () => {
        setError(null)
        setAgentsLoading(true)
        setAgentNonce((n) => n + 1)
      },

      selection,
      selectedCount: selection.size,
      isSelected: (id: string) => selection.has(id),
      // Every mutator uses the updater form. Not reachable from today's UI —
      // one click, one handler — but the moment two of these fire from the same
      // event, the value form silently drops one and there is no symptom.
      toggleSelected: (id: string) => {
        setUndone(null)
        setSelection((prev) => toggleOne(prev, id))
      },
      /**
       * Returns how many were genuinely new, so "Add" can report the overlap —
       * 13 on screen, 6 already on the list, 7 added — rather than leaving Renee
       * to trust that ticking two overlapping filters did not double anyone up.
       */
      addToSelection: (ids: readonly string[]) => {
        const added = addAll(selection, ids).added
        if (added.length) {
          setUndone(null)
          setSelection((prev) => addAll(prev, ids).next)
        }
        return added.length
      },
      /**
       * Undoable, because this is the other way to empty the list: on the All
       * tab with no filters, "Take off" covers the whole selection. Clear all
       * having an undo and this not having one, four lines apart in the same
       * menu, is not a distinction anyone can be expected to hold.
       */
      removeFromSelection: (ids: readonly string[]) => {
        const removed = removeAll(selection, ids).removed
        if (removed.length) {
          setUndone({ previous: selection, selectedOnly, count: removed.length, what: 'removed' })
          setSelection((prev) => removeAll(prev, ids).next)
        }
        return removed.length
      },
      clearSelection: (cause: 'cleared' | 'sent' = 'cleared') => {
        if (selection.size === 0) return
        setUndone({ previous: selection, selectedOnly, count: selection.size, what: cause })
        setSelection(EMPTY_SELECTION)
      },
      /**
       * Restores the scope as well as the list.
       *
       * Clearing turns "Show only these" off, because an empty list behind that
       * toggle is a blank screen with no way out. So an undo that put back 273
       * people and left the scope off would leave the toolbar reading
       * "Mailing list · 273" over a roster of 785 — and the next Tag would write
       * 785 rows.
       */
      undoSelection: undone
        ? {
            count: undone.count,
            what: undone.what,
            run: () => {
              setSelection(undone.previous)
              setSelectedOnlyRaw(undone.selectedOnly && undone.previous.size > 0)
              setUndone(null)
            },
          }
        : null,
      selectedOnly,
      setSelectedOnly: (v: boolean) => setSelectedOnlyRaw(v && selection.size > 0),
    }
  }, [derived, agents, error, tagsByAgent, tagsError, tagsLoaded, tagsLoading, agentsLoading, notesByAgent, search, filters, sortKey, includeDeparted, selection, selectedOnly, undone])

  return <RosterContext.Provider value={value}>{children}</RosterContext.Provider>
}

export function useRoster() {
  const ctx = useContext(RosterContext)
  if (!ctx) throw new Error('useRoster must be used inside RosterProvider')
  return ctx
}
